import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createDeviceOperation,
  createDeviceOperationAndDom,
  deleteDeviceOperation,
  fetchDetailDeviceOperation,
  fetchDeviceOperationList,
  updateDeviceOperation,
  updateDeviceOperationAndDom,
} from '@/domains/scenario/apis/device-management';
import type {
  DeviceOperationCreateDOMResponse,
  DeviceOperationDetail,
  DeviceOperationDetailResponse,
  DeviceOperationListResponse,
  DeviceOperationUpdateDOMRequest,
} from '@/domains/scenario/types/device-management-types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

interface UseDeviceManagementParams {
  deviceOperationId?: number | null | string;
  deviceOperationListQueryParams?: string;
  onCreateSuccess?: (data: DeviceOperationDetail) => void;
  onUpdateSuccess?: (data: DeviceOperationDetailResponse) => void;
  onDeleteSuccess?: () => void;
}

interface UseDeviceManagementReturn {
  deviceOperationListData: DeviceOperationListResponse | undefined;
  detailDeviceOperationData: DeviceOperationDetailResponse | undefined;
  createDeviceOperation: (
    data: Omit<DeviceOperationUpdateDOMRequest, 'device_operation_id'>
  ) => void;
  updateDeviceOperation: (data: DeviceOperationUpdateDOMRequest) => void;
  deleteDeviceOperation: (id: number | string) => void;
  createDeviceOperationAndDom: (data: DeviceOperationUpdateDOMRequest) => void;
  updateDeviceOperationAndDom: (data: DeviceOperationUpdateDOMRequest) => void;
}

const useDeviceManagement = ({
  deviceOperationId = null,
  deviceOperationListQueryParams = '',
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  onDeleteSuccess = () => {},
}: UseDeviceManagementParams): UseDeviceManagementReturn => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: deviceOperationListData } = useQuery({
    queryKey: ['deviceOperationListData', deviceOperationListQueryParams],
    queryFn: () =>
      fetchDeviceOperationList(
        deviceOperationListQueryParams
      ) as Promise<DeviceOperationListResponse>,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const { data: detailDeviceOperationData, isLoading: _isLoadingDetailDevice } = useQuery({
    queryKey: ['detailDeviceOperationData', deviceOperationId],
    queryFn: () =>
      fetchDetailDeviceOperation(
        deviceOperationId as string | undefined
      ) as Promise<DeviceOperationDetailResponse>,
    enabled: !!deviceOperationId,
    retry: false,
  });

  const createDeviceOperationMutation = useMutation<
    DeviceOperationDetailResponse,
    Error,
    DeviceOperationUpdateDOMRequest
  >({
    mutationFn: (deviceOperationData: DeviceOperationUpdateDOMRequest) =>
      createDeviceOperation(deviceOperationData) as Promise<DeviceOperationDetailResponse>,
    onSuccess: (responseData: DeviceOperationDetailResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['deviceOperationListData', deviceOperationListQueryParams],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    onError: (err: Error) => new NoticeMessage(t(err.message)),
  });

  const updateDeviceOperationMutation = useMutation<
    DeviceOperationDetailResponse,
    Error,
    DeviceOperationUpdateDOMRequest
  >({
    mutationFn: (deviceOperationData: DeviceOperationUpdateDOMRequest) =>
      updateDeviceOperation(
        deviceOperationData as unknown as Record<string, unknown> & {
          device_operation_id: string | number;
        }
      ) as Promise<DeviceOperationDetailResponse>,
    onSuccess: (responseData: DeviceOperationDetailResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['deviceOperationListData', deviceOperationListQueryParams],
          });
          onUpdateSuccess(responseData);
        },
      });
    },
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    onError: (err: Error) => new NoticeMessage(t(err.message)),
  });

  const deleteDeviceOperationMutation = useMutation({
    mutationFn: (deviceOperationId: number | string) => deleteDeviceOperation(deviceOperationId),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 삭제되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['deviceOperationListData', deviceOperationListQueryParams],
          });
          onDeleteSuccess();
        },
      });
    },
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    onError: (err: Error) => new NoticeMessage(t(err.message)),
  });

  const createDeviceOperationAndDomMutation = useMutation<
    DeviceOperationCreateDOMResponse,
    Error,
    DeviceOperationUpdateDOMRequest
  >({
    mutationFn: (deviceOperationData: DeviceOperationUpdateDOMRequest) =>
      createDeviceOperationAndDom(deviceOperationData) as Promise<DeviceOperationCreateDOMResponse>,
    onSuccess: (responseData: DeviceOperationCreateDOMResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['deviceOperationListData', deviceOperationListQueryParams],
          });
          // API create-and-dom response includes full DeviceOperationDetail beyond DeviceOperationCreateDOMData
          onCreateSuccess(responseData.data as unknown as DeviceOperationDetail);
        },
      });
    },
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    onError: (err: Error) => new NoticeMessage(t(err.message)),
  });

  const updateDeviceOperationAndDomMutation = useMutation<
    DeviceOperationDetailResponse,
    Error,
    DeviceOperationUpdateDOMRequest
  >({
    mutationFn: (deviceOperationData: DeviceOperationUpdateDOMRequest) =>
      updateDeviceOperationAndDom(deviceOperationData) as Promise<DeviceOperationDetailResponse>,
    onSuccess: (responseData: DeviceOperationDetailResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['deviceOperationListData', deviceOperationListQueryParams],
          });
          onUpdateSuccess(responseData);
        },
      });
    },
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    onError: (err: Error) => new NoticeMessage(t(err.message)),
  });

  return {
    deviceOperationListData,
    detailDeviceOperationData,
    createDeviceOperation: createDeviceOperationMutation.mutate,
    updateDeviceOperation: updateDeviceOperationMutation.mutate,
    deleteDeviceOperation: deleteDeviceOperationMutation.mutate,
    createDeviceOperationAndDom: createDeviceOperationAndDomMutation.mutate,
    updateDeviceOperationAndDom: updateDeviceOperationAndDomMutation.mutate,
  };
};

export default useDeviceManagement;
